import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [rightsModal, setRightsModal] = useState(false);
  const [rightsUser, setRightsUser] = useState(null);
  const [permissionOptions, setPermissionOptions] = useState({ modules: [], actions: [] });
  const [additionalPermissions, setAdditionalPermissions] = useState([]);
  const [rightsLoading, setRightsLoading] = useState(false);
  
  const { hasPermission, user: currentUser } = useAuth();

  const emptyForm = { firstName: '', lastName: '', email: '', phone: '', password: '', role: '', branch: '' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchBranches();
  }, []);

  const fetchUsers = () => api.get('/users').then(res => setUsers(res.data.data));
  const fetchRoles = () => api.get('/roles').then(res => setRoles(res.data.data));
  const fetchBranches = () => api.get('/branches').then(res => setBranches(res.data.data));

  const openRights = async (user) => {
    setRightsUser(user);
    setRightsLoading(true);
    try {
      const [optionsRes, userRes] = await Promise.all([
        api.get('/users/permission-options'),
        api.get(`/users/${user._id}/additional-permissions`)
      ]);
      setPermissionOptions(optionsRes.data.data || { modules: [], actions: [] });
      setAdditionalPermissions(userRes.data.data?.additionalPermissions || []);
      setRightsModal(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load user rights');
    } finally {
      setRightsLoading(false);
    }
  };

  const hasExtra = (module, action) => additionalPermissions.find(p => p.module === module)?.actions?.includes(action);
  const toggleRight = (module, action) => {
    setAdditionalPermissions(prev => {
      const next = prev.map(p => ({ ...p, actions: [...p.actions] }));
      let perm = next.find(p => p.module === module);
      if (!perm) { perm = { module, actions: [] }; next.push(perm); }
      if (perm.actions.includes(action)) perm.actions = perm.actions.filter(a => a !== action);
      else perm.actions.push(action);
      return next.filter(p => p.actions.length);
    });
  };

  const saveRights = async () => {
    if (!rightsUser) return;
    setRightsLoading(true);
    try {
      const res = await api.put(`/users/${rightsUser._id}/additional-permissions`, { additionalPermissions });
      setRightsUser(res.data.data);
      setRightsModal(false);
      fetchUsers();
      alert('Additional rights saved successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save rights');
    } finally {
      setRightsLoading(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setError('');
    setModal(true);
  };

  const openEdit = (user) => {
    setEditId(user._id);
    setForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      password: '', // Don't populate password on edit
      role: user.role?._id || '',
      branch: user.branch?._id || '',
    });
    setError('');
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // If editing, remove password from payload if it's empty
      const payload = { ...form };
      if (editId && !payload.password) {
        delete payload.password;
      }

      if (editId) {
        await api.put(`/users/${editId}`, payload);
      } else {
        await api.post('/users', payload);
      }
      
      setModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleToggleActive = async (user) => {
    // Prevent user from deactivating themselves
    if (user._id === currentUser._id) {
      return alert("You cannot deactivate your own account!");
    }
    
    const action = user.isActive ? 'DEACTIVATE' : 'ACTIVATE';
    if (!window.confirm(`Are you sure you want to ${action} ${user.firstName} ${user.lastName}?`)) return;

    try {
      await api.put(`/users/${user._id}`, { isActive: !user.isActive });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed');
    }
  };

  // --- ADDED: DELETE FUNCTION ---
  const handleDelete = async (user) => {
    if (user._id === currentUser._id) {
      return alert("You cannot delete your own account!");
    }

    if (!window.confirm(`WARNING: Are you sure you want to permanently DELETE ${user.firstName} ${user.lastName}? This action cannot be undone.`)) return;

    try {
      await api.delete(`/users/${user._id}`);
      fetchUsers(); // Refresh list after deletion
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const columns = [
    { header: 'Name', render: (row) => (
      <div>
        <span className="font-bold">{row.firstName} {row.lastName}</span>
        {row._id === currentUser._id && <span className="badge badge-info ml-1">You</span>}
      </div>
    )},
    { header: 'Email', accessor: 'email' },
    { header: 'Phone', accessor: 'phone' },
    { header: 'Role', render: (row) => row.role?.name || 'N/A' },
    { header: 'Branch', render: (row) => row.branch?.name || <span className="text-muted">Unassigned</span> },
    { header: 'Status', render: (row) => (
      <span className={`badge badge-${row.isActive ? 'success' : 'danger'}`}>
        {row.isActive ? 'Active' : 'Inactive'}
      </span>
    )},
    { header: 'Actions', render: (row) => (
      <div className="flex gap-1">
        {hasPermission('users', 'update') && (
          <button className="btn btn-sm btn-outline" onClick={() => openEdit(row)}>Edit</button>
        )}
        
        {hasPermission('users', 'update') && currentUser?.role?.name?.toLowerCase() === 'admin' && (
          <button className="btn btn-sm btn-outline" onClick={() => openRights(row)}>Rights</button>
        )}

        {hasPermission('users', 'update') && row._id !== currentUser._id && (
          <button 
            className={`btn btn-sm ${row.isActive ? 'btn-warning' : 'btn-success'}`} // Changed to btn-warning for better UX
            onClick={() => handleToggleActive(row)}
          >
            {row.isActive ? 'Deactivate' : 'Activate'}
          </button>
        )}

        {/* --- ADDED: DELETE BUTTON --- */}
        {hasPermission('users', 'delete') && row._id !== currentUser._id && (
          <button 
            className="btn btn-sm btn-danger" 
            onClick={() => handleDelete(row)}
            title="Permanently delete user"
          >
            Delete
          </button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h3>User Management ({users.length} Users)</h3>
        {hasPermission('users', 'create') && (
          <button className="btn btn-primary" onClick={openCreate}>+ Add New User</button>
        )}
      </div>
      
      <div className="card">
        <DataTable columns={columns} data={users} />
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editId ? "Edit System User" : "Create System User"} size="600px">
        <form onSubmit={handleSubmit}>
          {error && <div style={{ color: 'red', marginBottom: 15, background: '#fee2e2', padding: 10, borderRadius: 4 }}>{error}</div>}
          
          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>First Name *</label>
              <input className="form-control" required value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input className="form-control" required value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} />
            </div>
          </div>

          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>Email Address *</label>
              <input type="email" className="form-control" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input className="form-control" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
          </div>

          <div className="form-group">
            <label>{editId ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
            <input 
              type="password" 
              className="form-control" 
              minLength={8}
              required={!editId} 
              value={form.password} 
              onChange={e => setForm({...form, password: e.target.value})} 
              placeholder={editId ? "Min 8 chars (uppercase, lowercase, number)" : ""}
            />
            {!editId && <span className="text-xs text-muted">Must contain uppercase, lowercase, and a number</span>}
          </div>

          <div className="grid grid-2 gap-1">
            <div className="form-group">
              <label>System Role *</label>
              <select className="form-control" required value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                <option value="">Select Role</option>
                {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assigned Branch</label>
              <select className="form-control" value={form.branch} onChange={e => setForm({...form, branch: e.target.value})}>
                <option value="">None (Global Access)</option>
                {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-1 mt-2">
            <button type="submit" className="btn btn-primary w-full">
              {editId ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={rightsModal} onClose={() => setRightsModal(false)} title={`Additional Rights — ${rightsUser?.firstName || ''} ${rightsUser?.lastName || ''}`} size="900px">
        {rightsLoading ? (
          <div className="p-2">Loading permissions...</div>
        ) : (
          <>
            <div style={{ marginBottom: 15, padding: 12, background: '#f8fafc', borderRadius: 6 }}>
              <strong>How this works:</strong> These rights are added to the user's existing role. They do not replace the user's role or branch assignment.
            </div>
            <div className="table-container">
              <table>
                <thead><tr><th>Module</th>{permissionOptions.actions.map(a => <th key={a} style={{textTransform:'capitalize'}}>{a}</th>)}</tr></thead>
                <tbody>
                  {permissionOptions.modules.map(module => (
                    <tr key={module}>
                      <td className="font-bold">{module}</td>
                      {permissionOptions.actions.map(action => (
                        <td key={action} style={{textAlign:'center'}}>
                          <input type="checkbox" checked={!!hasExtra(module, action)} onChange={() => toggleRight(module, action)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-1 mt-2">
              <button type="button" className="btn btn-primary w-full" onClick={saveRights} disabled={rightsLoading}>Save Additional Rights</button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}